import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import {
  ConfirmDeletionModal,
  LegislationText,
  LegislationEntry,
  ModalDialog,
  Mtds,
  parseLegislationReferences,
  PlusIcon,
  ScalesIcon,
  TrashIcon,
  toUserMessage,
  useAddPlanLegislation,
  useDeletePlanLegislation,
  useLegislationCatalogue,
  useLegislationPreview,
  usePlanLegislation,
  type PlanLegislation,
  type SinglePlan
} from 'prove-common'
import styles from './PlanLegislationTab.module.css'

function AddLegislationModal({
  planId,
  onClose
}: Readonly<{ planId: number; onClose: () => void }>) {
  const dialogId = useId()
  const headingId = useId()
  const catalogue = useLegislationCatalogue(true)
  const [query, setQuery] = useState('')
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>()
  const preview = useLegislationPreview(selectedDocumentId)
  const [selected, setSelected] = useState<string[]>([])
  const [saveError, setSaveError] = useState<string>()
  const saving = useRef(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const previewHeadingRef = useRef<HTMLHeadingElement>(null)
  const lastChoiceRef = useRef<string | undefined>(undefined)
  const catalogueScrollRef = useRef({ panel: 0, results: 0 })
  const add = useAddPlanLegislation()
  const document = preview.data
  const selectedLegislation =
    document?.legislation ??
    catalogue.data?.documents.find(
      (item) => item.documentId === selectedDocumentId
    )
  const documents =
    catalogue.data?.documents.filter((item) =>
      [item.title, item.shortTitle, item.code].some((text) =>
        text
          .toLocaleLowerCase('nb-NO')
          .includes(query.trim().toLocaleLowerCase('nb-NO'))
      )
    ) ?? []
  const selectedLabels =
    document && selected.length > 0
      ? parseLegislationReferences(selected, document.html)
      : []

  useLayoutEffect(() => {
    if (selectedDocumentId) {
      previewHeadingRef.current?.focus({ preventScroll: true })
      return
    }
    if (!lastChoiceRef.current || !panelRef.current) return
    panelRef.current.scrollTop = catalogueScrollRef.current.panel
    if (resultsRef.current)
      resultsRef.current.scrollTop = catalogueScrollRef.current.results
    const choice = Array.from(
      panelRef.current.querySelectorAll<HTMLButtonElement>(
        'button[data-document-id]'
      )
    ).find((button) => button.dataset.documentId === lastChoiceRef.current)
    const target =
      choice ??
      panelRef.current.querySelector<HTMLInputElement>('input[type="search"]')
    target?.focus({ preventScroll: true })
  }, [selectedDocumentId])

  function chooseLegislation(documentId: string) {
    catalogueScrollRef.current = {
      panel: panelRef.current?.scrollTop ?? 0,
      results: resultsRef.current?.scrollTop ?? 0
    }
    lastChoiceRef.current = documentId
    setSelectedDocumentId(documentId)
  }

  async function save() {
    if (!document || saving.current) return
    saving.current = true
    setSaveError(undefined)
    try {
      await add.mutateAsync({
        planId,
        body: {
          documentId: document.legislation.documentId,
          selectedPartIds: selected
        }
      })
      Mtds.toast.success('Regelverket er lagt til')
      onClose()
    } catch (error) {
      setSaveError(toUserMessage(error, 'legge til regelverk'))
    } finally {
      saving.current = false
    }
  }

  return (
    <ModalDialog
      id={dialogId}
      open
      aria-labelledby={headingId}
      className={styles.dialog}
      closedby='closerequest'
      onCancel={(event) => {
        event.preventDefault()
        if (!saving.current) onClose()
      }}
      onClose={() => {
        if (!saving.current) onClose()
      }}
    >
      <Mtds.Button
        command='close'
        commandfor={dialogId}
        aria-label='Lukk'
        disabled={add.isPending}
        onClick={onClose}
      />
      <Mtds.Grid data-gap='5' className={styles.dialogBody}>
        <Mtds.Heading as='h2' id={headingId} data-size='sm'>
          Legg til regelverk
        </Mtds.Heading>
        <Mtds.Grid data-gap='0' className={styles.stepViewport}>
          <Mtds.Grid
            key={selectedDocumentId ?? 'catalogue'}
            ref={panelRef}
            data-gap='5'
            data-legislation-step={selectedDocumentId ? 'preview' : 'catalogue'}
            className={`${styles.stepPanel} ${selectedDocumentId ? styles.previewStep : styles.catalogueStep}`}
          >
            {!selectedDocumentId ? (
              <>
                <Mtds.Grid data-gap='4'>
                  <Mtds.Field
                    as='input'
                    type='search'
                    label='Søk etter lov eller forskrift'
                    placeholder='For eksempel «salmonella» eller «FOR-2009-08-11-1117»'
                    value={query}
                    onChange={(event) => setQuery(event.currentTarget.value)}
                  />
                  {catalogue.isPending && (
                    <p role='status'>Henter regelverk…</p>
                  )}
                  {catalogue.isError && (
                    <Mtds.Alert data-color='danger' role='alert'>
                      Kunne ikke hente regelverk fra Lovdata.
                      <Mtds.Button onClick={() => void catalogue.refetch()}>
                        Prøv igjen
                      </Mtds.Button>
                    </Mtds.Alert>
                  )}
                </Mtds.Grid>
                <Mtds.Grid
                  ref={resultsRef}
                  className={styles.scroll}
                  data-gap='3'
                  aria-label='Lover og forskrifter'
                  aria-busy={catalogue.isPending}
                >
                  {catalogue.isPending &&
                    [0, 1, 2, 3].map((item) => (
                      <Mtds.Card
                        key={item}
                        data-pad='4'
                        aria-hidden='true'
                        data-catalogue-placeholder
                      >
                        <Mtds.Flex
                          data-align='center'
                          data-justify='space-between'
                          data-nowrap
                        >
                          <Mtds.Grid data-gap='2' data-self='full'>
                            <Mtds.Skeleton
                              className={`${styles.loadingSkeleton} ${styles.loadingTitle}`}
                            />
                            <Mtds.Skeleton className={styles.loadingSkeleton} />
                            <Mtds.Skeleton className={styles.loadingSkeleton} />
                          </Mtds.Grid>
                          <Mtds.Skeleton
                            data-fixed
                            className={`${styles.loadingSkeleton} ${styles.loadingButton}`}
                          />
                        </Mtds.Flex>
                      </Mtds.Card>
                    ))}
                  {documents.map((item) => (
                    <Mtds.Card key={item.documentId} data-pad='4'>
                      <Mtds.Flex
                        data-align='center'
                        data-justify='space-between'
                        data-nowrap
                      >
                        <Mtds.Grid
                          data-gap='1'
                          data-self='full'
                          className={styles.resultText}
                        >
                          <Mtds.Heading as='h3' data-size='2xs'>
                            {item.shortTitle}
                          </Mtds.Heading>
                          <Mtds.Muted>
                            {item.code} · {item.title}
                          </Mtds.Muted>
                        </Mtds.Grid>
                        <Mtds.Button
                          data-fixed
                          data-nowrap
                          aria-label={`Velg ${item.shortTitle}`}
                          data-document-id={item.documentId}
                          onClick={() => chooseLegislation(item.documentId)}
                        >
                          Velg
                        </Mtds.Button>
                      </Mtds.Flex>
                    </Mtds.Card>
                  ))}
                  {catalogue.isSuccess && documents.length === 0 && (
                    <p>Ingen regelverk funnet.</p>
                  )}
                </Mtds.Grid>
              </>
            ) : (
              <>
                <Mtds.Flex
                  data-align='start'
                  data-justify='space-between'
                  data-nowrap
                >
                  <Mtds.Info data-self='full' className={styles.resultText}>
                    <ScalesIcon aria-hidden />
                    <strong>{selectedLegislation?.code ?? 'Regelverk'}</strong>
                    {selectedLegislation?.title}
                  </Mtds.Info>
                  <Mtds.Button
                    data-fixed
                    data-nowrap
                    disabled={add.isPending}
                    onClick={() => {
                      setSelectedDocumentId(undefined)
                      setSelected([])
                      setSaveError(undefined)
                    }}
                  >
                    Bytt regelverk
                  </Mtds.Button>
                </Mtds.Flex>
                <Mtds.Grid data-gap='3'>
                  <Mtds.Heading
                    as='h3'
                    data-size='2xs'
                    ref={previewHeadingRef}
                    tabIndex={-1}
                  >
                    <ScalesIcon aria-hidden />
                    Valgt hjemmel
                  </Mtds.Heading>
                  <Mtds.Flex data-gap='2' aria-live='polite'>
                    {selected.length === 0 ? (
                      <Mtds.Tag data-icon='none'>Hele regelverket</Mtds.Tag>
                    ) : (
                      selectedLabels.map((part) => (
                        <Mtds.Chip
                          as='button'
                          type='button'
                          key={part.id}
                          data-removable
                          disabled={add.isPending}
                          aria-label={`Fjern ${part.label}`}
                          onClick={() =>
                            setSelected((ids) =>
                              ids.filter((id) => id !== part.id)
                            )
                          }
                        >
                          {part.label}
                        </Mtds.Chip>
                      ))
                    )}
                  </Mtds.Flex>
                </Mtds.Grid>
                <p>
                  Klikk på merkelappene i lovteksten for å velge hvilke
                  bestemmelser planen er hjemlet i. Uten valg legges hele
                  regelverket til.
                </p>
                <Mtds.Group className={styles.scroll} aria-label='Lovtekst'>
                  {preview.isPending && (
                    <Mtds.Grid data-gap='4'>
                      <p role='status'>Henter lovtekst…</p>
                      <Mtds.Skeleton
                        className={`${styles.loadingSkeleton} ${styles.loadingTitle}`}
                        aria-hidden
                      />
                      <Mtds.Skeleton
                        className={styles.loadingSkeleton}
                        aria-hidden
                      />
                      <Mtds.Skeleton
                        className={styles.loadingSkeleton}
                        aria-hidden
                      />
                      <Mtds.Skeleton
                        className={styles.loadingSkeleton}
                        aria-hidden
                      />
                    </Mtds.Grid>
                  )}
                  {preview.isError && (
                    <Mtds.Alert data-color='danger' role='alert'>
                      Kunne ikke hente lovteksten.
                      <Mtds.Button onClick={() => void preview.refetch()}>
                        Prøv igjen
                      </Mtds.Button>
                    </Mtds.Alert>
                  )}
                  {document && (
                    <LegislationText
                      document={document}
                      selected={selected}
                      onChange={add.isPending ? undefined : setSelected}
                    />
                  )}
                </Mtds.Group>
              </>
            )}
          </Mtds.Grid>
        </Mtds.Grid>
        {saveError && (
          <Mtds.Alert data-color='danger' role='alert'>
            {saveError}
          </Mtds.Alert>
        )}
      </Mtds.Grid>
      <footer>
        <Mtds.Flex data-justify='end' data-gap='3'>
          <Mtds.Button disabled={add.isPending} onClick={onClose}>
            Avbryt
          </Mtds.Button>
          <Mtds.Button
            data-variant='primary'
            disabled={!document || add.isPending}
            aria-busy={add.isPending}
            onClick={() => void save()}
          >
            Legg til
          </Mtds.Button>
        </Mtds.Flex>
      </footer>
    </ModalDialog>
  )
}

function LegislationCard({
  entry,
  plan
}: Readonly<{ entry: PlanLegislation; plan: SinglePlan }>) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<string>()
  const remove = useDeletePlanLegislation()
  const inherited = entry.sourcePlanId !== plan.id
  const canDelete = !inherited && plan.canManage

  return (
    <Mtds.Card
      data-pad='4'
      data-pad-sm='5'
      data-legislation-entry={entry.id}
      className={styles.legislationCard}
    >
      <Mtds.Grid data-gap='4'>
        <LegislationEntry
          entry={entry}
          actions={
            canDelete && (
              <Mtds.Button
                data-fixed
                data-nowrap
                data-variant='tertiary'
                data-color='danger'
                aria-label={`Slett ${entry.legislation.shortTitle}`}
                onClick={() => setConfirmDelete(true)}
              >
                <TrashIcon aria-hidden />
              </Mtds.Button>
            )
          }
          metadata={
            inherited && (
              <Mtds.Info>
                <strong>Arvet fra</strong>
                <Link to={`/plans/${entry.sourcePlanId}`}>
                  {entry.sourcePlanName || `Plan ${entry.sourcePlanId}`}
                </Link>
              </Mtds.Info>
            )
          }
        />
        {deleteError && (
          <Mtds.Alert data-color='danger' role='alert'>
            {deleteError}
          </Mtds.Alert>
        )}
      </Mtds.Grid>
      <ConfirmDeletionModal
        title={`Vil du fjerne ${entry.legislation.shortTitle} fra planen?`}
        isOpen={confirmDelete}
        setOpen={setConfirmDelete}
        isPending={remove.isPending}
        onConfirm={async () => {
          try {
            await remove.mutateAsync({
              planId: plan.id,
              entryId: entry.id,
              etag: entry.etag
            })
            setConfirmDelete(false)
            Mtds.toast.success('Regelverket er fjernet')
          } catch (error) {
            setConfirmDelete(false)
            setDeleteError(toUserMessage(error, 'fjerne regelverk'))
          }
        }}
      />
    </Mtds.Card>
  )
}

export function PlanLegislationTab({ plan }: Readonly<{ plan: SinglePlan }>) {
  const entries = usePlanLegislation(plan.id)
  const [adding, setAdding] = useState(false)
  return (
    <Mtds.Grid data-gap='5'>
      <Mtds.Flex data-justify='space-between' data-align='center'>
        <Mtds.Heading as='h2' data-size='sm'>
          Regelverk
        </Mtds.Heading>
        {plan.canManage && (
          <Mtds.Button data-variant='primary' onClick={() => setAdding(true)}>
            <PlusIcon aria-hidden />
            Legg til regelverk
          </Mtds.Button>
        )}
      </Mtds.Flex>
      <p>Planen er hjemlet i følgende lover og forskrifter.</p>
      {entries.isPending && <p role='status'>Henter planens regelverk…</p>}
      {entries.isError && (
        <Mtds.Alert data-color='danger' role='alert'>
          Kunne ikke hente planens regelverk.
          <Mtds.Button onClick={() => void entries.refetch()}>
            Prøv igjen
          </Mtds.Button>
        </Mtds.Alert>
      )}
      {entries.isSuccess && entries.data.length === 0 && (
        <p>Ingen lover eller forskrifter er lagt til i planen.</p>
      )}
      {entries.data?.map((entry) => (
        <LegislationCard key={entry.id} entry={entry} plan={plan} />
      ))}
      {adding && (
        <AddLegislationModal
          planId={plan.id}
          onClose={() => setAdding(false)}
        />
      )}
    </Mtds.Grid>
  )
}
